import { useEffect, useState, useMemo } from 'react';
import type { User } from 'oidc-client-ts';
import { manager } from '../stores/user/UserStore';

export interface IUserHookResult {
  user: User | undefined;
  isSuperAdmin: boolean;
}

/**
 * Custom hook for accessing the OIDC user object.
 * It encapsulates the logic for retrieving the user from the OIDC user manager.
 * @returns An object with the OIDC user object and an isSuperAdmin flag.
 */
export const useUser = (): IUserHookResult => {
  const [user, setUser] = useState<User | undefined>();

  useEffect(() => {
    const fetchUser = async () => {
      const userFromManager = await manager.getUser();
      setUser(userFromManager ?? undefined);
    };

    const onUserLoaded = (loadedUser: User) => {
      setUser(loadedUser);
    };

    const onUserUnloaded = () => {
      setUser(undefined);
    };

    manager.events.addUserLoaded(onUserLoaded);
    manager.events.addUserUnloaded(onUserUnloaded);

    fetchUser();

    return () => {
      manager.events.removeUserLoaded(onUserLoaded);
      manager.events.removeUserUnloaded(onUserUnloaded);
    };
  }, []);

  const isSuperAdmin = useMemo(() => (
    (() => {
      const profile = user?.profile as Record<string, unknown> | undefined;

      const rawApplicationId = profile?.["ApplicationId"];
      const applicationId =
        typeof rawApplicationId === "string"
          ? rawApplicationId
          : typeof rawApplicationId === "number"
            ? String(rawApplicationId)
            : "";

      const rawApplicationName = profile?.["ApplicationName"];
      const applicationName = typeof rawApplicationName === "string" ? rawApplicationName : "";

      return (
        applicationId === "2" &&
        ["georges@randmar.io", "d@randmar.io", "jpundyk@randmar.io"].includes(applicationName)
      );
    })()
  ), [user]);

  return { user, isSuperAdmin };
};
